import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import { Input, Button, Card, CardHeader, CardBody, CardTitle, FormGroup, Label, Row, Col, UncontrolledTooltip } from 'reactstrap';

import { DenyReasonOverlay } from './partials/DenyReasonOverlay';

import { setting, member } from '../../api';
import UserContext from '../UserContext';
import { FaSpinner } from 'react-icons/fa';
import { SettingsClearancesType } from 'api/settings/clearances';
import { MemberDB, MemberDocumentApprovalDB, MemberHeaderDB } from 'api/members';
import { MemberDocumentDB } from 'api/members/document';

interface Props {}

export const QuickApproval = (props: Props) => {
	const { jwt } = useContext(UserContext);

	const history = useHistory();

	const [isLoading, setIsLoading] = useState(true);
	const [clearanceHeaders, setClearanceHeaders] = useState<Array<SettingsClearancesType>>([]);
	const [memberHeaders, setMemberHeaders] = useState<Array<MemberHeaderDB>>([]);
	const [data, setData] = useState<Array<MemberDocumentApprovalDB>>([]);
	const [selectedDocument, setSelectedDocument] = useState<{
		memberData: MemberDB;
		docData: MemberDocumentDB;
		docURL: string;
	} | null>(null);
	const [isDenyOverlay, setIsDenyOverlay] = useState(false);

	const [isDocumentEditMode, setIsDocumentEditMode] = useState(false);
	const [docEditData, setDocEditData] = useState<MemberDocumentDB | null>(null);
	const [canSave, setCanSave] = useState(false);
	const [formSaveInProgress, setFormSaveInProgress] = useState(false);

	useEffect(() => {
		Promise.resolve()
			.then(() => {
				// Get all of the clearance headers
				return setting.clearances.get(jwt).then((data) => {
					setClearanceHeaders(data.types_list);
				});
			})
			.then(() => {
				// Get all of the member headers
				return member.getHeaders(jwt).then((data) => {
					setMemberHeaders(data);
				});
			})
			.then(async () => {
				// Get all of the members
				const responseObj = await member.listPendingApproval(jwt);
				setData(responseObj);
			});
	}, []);

	useEffect(() => {
		Promise.resolve().then(async () => {
			if (data.length > 0) {
				setIsLoading(true);

				// Get the details
				try {
					const responseObj = await member.getPendingApproval(jwt, data[0].docID);

					setSelectedDocument(responseObj);
				} catch (error) {
					// Skip it
					console.log(error);
				}
			} else {
				setSelectedDocument(null);
			}

			setIsLoading(false);
		});
	}, [data]);

	useEffect(() => {
		let canSave = true;

		if (docEditData == null) {
			return;
		}

		// Do the form validation
		if (docEditData.type == null || docEditData.certification_date == null) {
			canSave = false;
		}

		if (!clearanceHeaders.map((clearance) => clearance.uid).includes(docEditData.type)) {
			canSave = false;
		}

		const todaysDate = new Date();
		todaysDate.setHours(0, 0, 0, 0);

		if (todaysDate.getTime() < new Date(docEditData.certification_date).getTime()) {
			canSave = false;
		}

		setCanSave(canSave);
	}, [docEditData]);

	/**
	 * This function handles the document approval button click
	 */
	const handleApproveButtonClick = () => {
		return member.document
			.approve(jwt, selectedDocument!.memberData.docID, selectedDocument!.docData.docID)
			.then((response) => {
				// Success. Move to the next one
				const newData = [...data];
				newData.splice(0, 1);
				setData(newData);
			})
			.catch((error) => {
				console.log(error);
			});
	};

	/**
	 * This function handles the document deny button click
	 */
	const handleDenyButtonClick = () => {
		setIsDenyOverlay(true);
	};

	/**
	 * This function handles the document deny overlay submission
	 * @param {*} reasonText
	 */
	const handleDenySubmit = (reasonText: string) => {
		return member.document.deny(jwt, selectedDocument!.memberData.docID, selectedDocument!.docData.docID, reasonText).then((response) => {
			// Success. Move to the next one
			setIsDenyOverlay(false);
			const newData = [...data];
			newData.splice(0, 1);
			setData(newData);
		});
	};

	/**
	 * This function handles the next button click
	 */
	const handleNextButtonClick = () => {
		// Move to the next one
		setIsDenyOverlay(false);
		const newData = [...data];
		newData.splice(0, 1);
		setData(newData);
	};

	/**
	 * This function handles the document deny overlay cancel button click
	 */
	const handleDenyOverlayCancel = () => {
		setIsDenyOverlay(false);
	};

	/**
	 * This function handles the view member details click
	 */
	const handleViewMemberDetailsClick = () => {
		history.push(`/administrator/member/${selectedDocument!.memberData.docID}`);
	};

	/**
	 * This function handles the edit clearance data button click
	 */
	const handleToggleClearanceDataClick = () => {
		if (!isDocumentEditMode) {
			setDocEditData(selectedDocument!.docData);
			setIsDocumentEditMode(!isDocumentEditMode);
		} else {
			setIsDocumentEditMode(!isDocumentEditMode);
			setDocEditData(null);
		}
	};

	/**
	 * This function handles setting the new value for the document
	 */
	const handleDocEditInputFieldChange = async (fieldName: keyof MemberDocumentDB, newValue: any) => {
		let newDocument = { ...docEditData };

		(newDocument as any)[fieldName] = newValue;

		setDocEditData(newDocument as MemberDocumentDB);
	};

	/**
	 * This function handles saving the document attributes
	 */
	const handleOnSaveClick = async () => {
		try {
			const response = await member.document.fix(jwt, selectedDocument!.memberData.docID, selectedDocument!.docData.docID, docEditData!);

			// Success. Update the parent data
			const newDocument = { ...selectedDocument };
			(newDocument as any).docData = docEditData;
			setSelectedDocument(newDocument as any);
			handleToggleClearanceDataClick();
		} catch (error) {
			console.log(error);
		}
	};

	/**
	 * The render function
	 */
	return (
		<div className="content quick-approval">
			<DenyReasonOverlay
				isOpen={isDenyOverlay}
				onDeny={handleDenySubmit}
				onCancel={() => {
					handleDenyOverlayCancel();
				}}
			/>
			<Row>
				<Col md="12">
					<Card>
						<CardHeader>
							<Button
								className="pull-right"
								id={`quick-approval-deny-button`}
								color="danger"
								onClick={() => handleDenyButtonClick()}
								disabled={isLoading || selectedDocument == null}
							>
								<i className="fa fa-thumbs-down" /> {data != null && data.length > 1 ? 'Deny & Next' : 'Deny'}
							</Button>
							<Button
								className="pull-right"
								id={`quick-approval-next-button`}
								color="secondary"
								onClick={() => handleNextButtonClick()}
								disabled={isLoading || selectedDocument == null || (data != null && data.length === 1)}
							>
								<i className="fa fa-arrow-right" /> Next
							</Button>
							<Button
								className="pull-right"
								id={`quick-approval-approve-button`}
								color="success"
								onClick={() => handleApproveButtonClick()}
								disabled={isLoading || selectedDocument == null}
							>
								<i className="fa fa-thumbs-up" /> {data != null && data.length > 1 ? 'Approve & Next' : 'Approve'}
							</Button>

							<UncontrolledTooltip delay={0} target={`quick-approval-approve-button`}>
								Approve Document
							</UncontrolledTooltip>
							<UncontrolledTooltip delay={0} target={`quick-approval-deny-button`}>
								Reject Document
							</UncontrolledTooltip>
							<CardTitle tag="h4">Quick Approval</CardTitle>
						</CardHeader>
						<CardBody>
							<Row>
								{isLoading ? (
									<Col md={12}>Loading...</Col>
								) : (
									<>
										{selectedDocument != null ? (
											<>
												<Col md={6}>
													<h6>Uploaded Document</h6>

													{selectedDocument.docURL != null ? (
														<>
															{selectedDocument.docURL != null ? (
																<iframe
																	title={'quick-approval-frame'}
																	style={{ border: '0px' }}
																	src={`${selectedDocument.docURL}`}
																></iframe>
															) : (
																<iframe
																	title={'quick-approval-frame'}
																	style={{ border: '0px' }}
																	src={`data:application/pdf;base64`}
																></iframe>
															)}
														</>
													) : (
														<div>No document available to view</div>
													)}
												</Col>
												<Col className={'information-data'} md={6}>
													<Row>
														<Col className={'align-self-center'}>
															<h6 className="m-0">User Information</h6>
														</Col>
														<Col>
															<Button
																className="btn-sm m-0 pull-right"
																id={'quick-approval-view-member-details-button'}
																color="link"
																onClick={() => handleViewMemberDetailsClick()}
																disabled={isLoading || selectedDocument == null}
															>
																<i className="fa fa-share" />
															</Button>
															<UncontrolledTooltip delay={0} target={'quick-approval-view-member-details-button'}>
																View Member Details
															</UncontrolledTooltip>
														</Col>
													</Row>
													<Row>
														<Col>
															{Object.keys(memberHeaders).map((header) => {
																const { name } = memberHeaders[header as any];

																let val = selectedDocument.memberData[header];

																if (header === 'uid') {
																	val = selectedDocument.memberData[selectedDocument.memberData['uid']];
																}

																return (
																	<FormGroup row key={`quick-approve-user-info-${header}`}>
																		<Label sm={4}>
																			<strong>{name}</strong>
																		</Label>
																		<Col className={'data-value'} sm={8}>
																			{val}
																		</Col>
																	</FormGroup>
																);
															})}
														</Col>
													</Row>
													<Row>
														<Col className={'align-self-center'}>
															<h6 className="m-0">Clearance Data</h6>
														</Col>
														<Col>
															{isDocumentEditMode === true ? (
																<>
																	<Button
																		className="m-0 ml-1 pull-right"
																		size="sm"
																		color="link"
																		onClick={() => handleToggleClearanceDataClick()}
																	>
																		Cancel
																	</Button>
																	<Button
																		className="m-0 pull-right"
																		size="sm"
																		color="button"
																		disabled={!canSave || formSaveInProgress}
																		onClick={() => handleOnSaveClick()}
																	>
																		{formSaveInProgress ? <FaSpinner size={12} className="fa-spin" /> : 'Save'}
																	</Button>
																</>
															) : (
																<>
																	<Button
																		id={'quick-approval-edit-document-button'}
																		className="m-0 pull-right"
																		size="sm"
																		color="link"
																		onClick={() => handleToggleClearanceDataClick()}
																		disabled={isLoading || selectedDocument == null}
																	>
																		<i className="fa fa-pencil" />
																	</Button>
																	<UncontrolledTooltip delay={0} target={'quick-approval-edit-document-button'}>
																		Edit Document
																	</UncontrolledTooltip>
																</>
															)}
														</Col>
													</Row>
													<Row>
														<Col>
															<FormGroup row key={`quick-approve-form-data-type`}>
																<Label sm={4}>
																	<strong>Type</strong>
																</Label>
																<Col className={'data-value'} sm={5}>
																	{isDocumentEditMode === true ? (
																		<>
																			<Input
																				type={'select'}
																				name={'fieldName'}
																				id={'fieldName'}
																				placeholder={'description'}
																				onChange={(e) => {
																					handleDocEditInputFieldChange('type', e.target.value);
																				}}
																				defaultValue={docEditData!.type}
																			>
																				{clearanceHeaders.map((option) => {
																					return (
																						<option key={option.uid} value={option.uid}>
																							{option.name}
																						</option>
																					);
																				})}
																			</Input>
																		</>
																	) : (
																		clearanceHeaders.filter(
																			(clearance) => clearance.uid === selectedDocument.docData.type
																		)[0].name
																	)}
																</Col>
															</FormGroup>
															<FormGroup row key={`quick-approve-form-data-uploaded-date`}>
																<Label sm={4}>
																	<strong>Uploaded Date</strong>
																</Label>
																<Col className={'data-value'} sm={8}>
																	{selectedDocument.docData.uploaded_date}
																</Col>
															</FormGroup>
															<FormGroup row key={`quick-approve-form-data-certification_date`}>
																<Label sm={4}>
																	<strong>Certification Date</strong>
																</Label>
																<Col className={'data-value'} sm={5}>
																	{isDocumentEditMode === true ? (
																		<>
																			<Input
																				type={'date'}
																				name={'fieldName'}
																				id={'fieldName'}
																				placeholder={'description'}
																				onChange={(e) => {
																					handleDocEditInputFieldChange('certification_date', e.target.value);
																				}}
																				value={docEditData!.certification_date as any}
																			/>
																		</>
																	) : (
																		selectedDocument.docData.certification_date
																	)}
																</Col>
															</FormGroup>
														</Col>
													</Row>
												</Col>
											</>
										) : (
											<Col md={12}>No documents to review</Col>
										)}
									</>
								)}
							</Row>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</div>
	);
};
